import {
  Context,
  createContext,
  useContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
} from 'react'
import { ResumeExample } from '@rio/types'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { actions, BriefExampleCategory, selectors } from 'builder/modules/resumeEditor'

type ResumeExampleContextType = {
  jobTitles: string[]
  searchedTitle: string
  setSearchedTitle: (searchedTitle: string) => void
  categories: BriefExampleCategory[]
  selectedCategoryId: number
  setSelectedCategoryId: (setSelectedCategoryId: number) => void
  resumeExamples: ResumeExample[]
  selectedResumeExample: ResumeExample | null
  setSelectedResumeExample: (resumeExample: ResumeExample) => void
  fetchingResumeExamples: boolean
  currentPage: number
  totalPages: number
  loadMore: (resetPage?: boolean) => void
}

const ResumeExampleContext = createContext<ResumeExampleContextType | null>(null)

type ResumeExampleContextProviderProps = {
  selectedResumeExample: ResumeExample | null
  setSelectedResumeExample: (example: ResumeExample) => void
  children: ReactNode
}

const ResumeExampleContextProvider = (
  props: PropsWithChildren<ResumeExampleContextProviderProps>,
) => {
  const dispatch = useDispatch()
  const { selectedResumeExample, setSelectedResumeExample, children } = props
  const {
    fetchingResumeExamples,
    currentPage,
    totalPages,
    resumeExamples,
    categories,
    jobTitles,
    searchedTitle,
    selectedCategoryId,
  } = useTypedSelector(selectors.useExample)

  const loadMore = useCallback(
    (resetPage?: boolean) => {
      dispatch(actions.fetchResumeExamples({ resetPage }))
    },
    [dispatch],
  )

  const setSearchedTitle = useCallback(
    (value: string) => {
      dispatch(actions.setResumeExamplesData({ searchedTitle: value }))
    },
    [dispatch],
  )

  const setSelectedCategoryId = useCallback(
    (value: number) => {
      dispatch(actions.setResumeExamplesData({ selectedCategoryId: value }))
    },
    [dispatch],
  )

  return (
    <ResumeExampleContext.Provider
      value={{
        jobTitles,
        searchedTitle,
        setSearchedTitle,
        categories,
        selectedCategoryId,
        setSelectedCategoryId,
        resumeExamples,
        selectedResumeExample,
        setSelectedResumeExample,
        fetchingResumeExamples,
        currentPage,
        totalPages,
        loadMore,
      }}
    >
      {children}
    </ResumeExampleContext.Provider>
  )
}

const useResumeExampleContext = (): ResumeExampleContextType => {
  const context = useContext<ResumeExampleContextType>(
    ResumeExampleContext as Context<ResumeExampleContextType>,
  )

  if (!context) {
    throw new Error('No context data found')
  }

  return context
}

export default ResumeExampleContext
export { useResumeExampleContext, ResumeExampleContextProvider }
